import { useEffect, useState } from "react";
import { portalFetch } from "../api/client";

type DeliveryState = "checking" | "unsupported" | "unconfigured" | "off" | "on" | "denied" | "error";

function serviceWorkerReady(timeoutMs = 10_000) {
	return Promise.race([
		navigator.serviceWorker.ready,
		new Promise<never>((_, reject) => setTimeout(() => reject(new Error("service worker unavailable")), timeoutMs)),
	]);
}

function applicationServerKey(value: string) {
	const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), "=");
	const bytes = atob(padded.replace(/-/g, "+").replace(/_/g, "/"));
	return Uint8Array.from(bytes, (character) => character.charCodeAt(0));
}

export function NotificationControl({ publicKey }: { publicKey?: string }) {
	const [state, setState] = useState<DeliveryState>("checking");
	useEffect(() => {
		if (!publicKey) {
			setState("unconfigured");
			return;
		}
		if (!("serviceWorker" in navigator) || !("PushManager" in window) || !("Notification" in window)) {
			setState("unsupported");
			return;
		}
		serviceWorkerReady()
			.then((registration) => registration.pushManager.getSubscription())
			.then((subscription) =>
				setState(subscription ? "on" : Notification.permission === "denied" ? "denied" : "off"),
			)
			.catch(() => setState("error"));
	}, [publicKey]);

	async function enable() {
		if (!publicKey) return;
		try {
			const permission =
				Notification.permission === "default" ? await Notification.requestPermission() : Notification.permission;
			if (permission !== "granted") {
				setState("denied");
				return;
			}
			const registration = await serviceWorkerReady();
			const subscription =
				(await registration.pushManager.getSubscription()) ??
				(await registration.pushManager.subscribe({
					userVisibleOnly: true,
					applicationServerKey: applicationServerKey(publicKey),
				}));
			const json = subscription.toJSON();
			if (!json.endpoint || !json.keys?.p256dh || !json.keys.auth) throw new Error("Incomplete push subscription");
			await portalFetch<void>("/api/portal/v1/push-subscriptions", {
				method: "POST",
				body: JSON.stringify({ endpoint: json.endpoint, keys: { p256dh: json.keys.p256dh, auth: json.keys.auth } }),
			});
			setState("on");
		} catch {
			setState("error");
		}
	}

	async function disable() {
		try {
			const registration = await serviceWorkerReady();
			const subscription = await registration.pushManager.getSubscription();
			if (subscription) {
				await portalFetch<void>("/api/portal/v1/push-subscriptions", {
					method: "DELETE",
					body: JSON.stringify({ endpoint: subscription.endpoint }),
				});
				await subscription.unsubscribe();
			}
			setState("off");
		} catch {
			setState("error");
		}
	}

	const copy: Record<DeliveryState, string> = {
		checking: "Checking this browser…",
		unsupported: "This browser does not support project alerts. The Inbox still works here.",
		unconfigured: "Browser permission may be on, but Portal alert delivery is not configured on the server yet.",
		off: "Alerts are off for this browser.",
		on: "Alerts are on for this browser.",
		denied: "Browser permission is blocked. Allow notifications in site settings, then try again.",
		error: "Alert settings could not be updated. Your Inbox is still available here.",
	};
	return (
		<section className="notification-control" aria-labelledby="notification-control-heading">
			<div>
				<p className="eyebrow">DELIVERY</p>
				<h3 id="notification-control-heading">Browser alerts</h3>
				<p role="status">{copy[state]}</p>
				<small>Alerts announce new Inbox items; enabling them does not create project activity.</small>
			</div>
			{state === "off" && (
				<button type="button" onClick={enable}>
					Enable alerts
				</button>
			)}
			{state === "denied" && (
				<button type="button" onClick={enable}>
					Check permission again
				</button>
			)}
			{state === "on" && (
				<button type="button" className="secondary" onClick={disable}>
					Turn off alerts
				</button>
			)}
		</section>
	);
}
